import React from 'react';

import { useIntl } from 'react-intl';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { ICON_MAP } from 'src/utils/constants';
import { toast } from 'src/hooks/useToast';
import { useOnError } from 'src/utils/errors';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { servicePlansMessages } from 'src/features/servicePlans/utils/messages';
import { useDeactivateServicePlanItem } from 'src/features/servicePlanItems/api/useDeactivateServicePlanItem';
import { SERVICE_PLAN_ITEM_RESOURCE } from 'src/features/servicePlanItems/api/useGetServicePlanItem';
import { LIST_SERVICE_PLAN_ITEMS_RESOURCE } from 'src/features/servicePlanItems/api/useListServicePlanItems';
import { servicePlanItemsMessages } from 'src/features/servicePlanItems/utils/messages';
import { ServicePlanItem } from 'src/features/servicePlanItems/utils/types';

interface DeactivateServicePlanItemFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedServicePlanItem: ServicePlanItem;
    children?: React.ReactNode;
}

function DeactivateServicePlanItemFormModal<TData>({
    selectedServicePlanItem,
    children,
}: DeactivateServicePlanItemFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();

    const onError = useOnError();

    const form = useForm({
        resolver: zodResolver(z.object({})),
    });

    const { mutate: deactivateServicePlanItem } = useDeactivateServicePlanItem({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [SERVICE_PLAN_ITEM_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [LIST_SERVICE_PLAN_ITEMS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(servicePlanItemsMessages.deactivateServicePlanItemSuccess),
                description: formatMessage(servicePlanItemsMessages.deactivateServicePlanItemSuccessDescription),
            });
        },
        onError: (error) => onError(error, form.setError),
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...servicePlanItemsMessages.deactivateServicePlanItem },
                subTitle: {
                    ...servicePlanItemsMessages.deactivateServicePlanItemSubtitle,
                    values: {
                        name: selectedServicePlanItem.name,
                    },
                },
                icon: ICON_MAP.TOGGLE_OFF,
                confirmationMessage: { ...servicePlanItemsMessages.confirmDeactivateServicePlanItem },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: servicePlansMessages.deactivate,
                callbackFn: () => {
                    return (
                        deactivateServicePlanItem({
                            planItemId: selectedServicePlanItem.id,
                        }),
                        setIsModalOpen(false)
                    );
                },
            }}
            children={children}
        />
    );
}

export default DeactivateServicePlanItemFormModal;
